import { ActivityFilterKey } from '@gripp/shared-logic';
import {
  ActivityListBase,
  AddIcon,
  Button,
  SlideAction,
  tokens,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useLocation, useNavigate } from 'react-router-dom';
import { ActivityFoldOneColumn } from '../../activity/components/activityFoldOneColumn';
import { ActivitiesProps } from '../../activity/components/activityList';
import { useAssetRouting } from '../assetRouteContext';
import { isActivityOrEditPage } from '../assetUtils';
import { ViewAllSectionHeader } from './viewAllSectionHeader';

export type AssetActivityFoldProps = ActivitiesProps & {
  assetId: string;
  issueId?: string;
  forceShowing?: boolean;
};

export const AssetActivityFold = (props: AssetActivityFoldProps) => {
  // issueId comes from the nearest AssetRouteProvider — set by every component
  // that renders AssetCard (both left and right columns).
  const { issueId } = useAssetRouting(props.assetId);
  const location = useLocation();
  const isOnAssetPage = location.pathname.startsWith('/assets/');

  if (!props.forceShowing && issueId && isOnAssetPage) {
    return (
      <ActivityFoldOneColumn
        assetId={props.assetId}
        issueId={issueId}
        onIssueSelected={props.onIssueSelected}
        selectedIssue={issueId}
        showAssetName={false}
        hideFilterBar={true}
      />
    );
  }

  return <AssetActivityFoldCompact {...props} issueId={issueId} />;
};

const AssetActivityFoldCompact = ({
  assetId,
  issueId,
  onIssueSelected,
}: AssetActivityFoldProps) => {
  const LIST_HEIGHT = 70;
  const LIST_CONTAINER_BOTTOM_PADDING = 20;
  const { t } = useTranslation();
  const { prepareSlideTransition } = useSlideLayout();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath } = useAssetRouting(assetId);
  const [dataLoaded, setDataLoaded] = useState('');
  const [listContainerHeight, setListContainerHeight] = useState(LIST_HEIGHT);

  const handleDataLoaded = useCallback((data: Array<{ id: string }>) => {
    if (data?.length > 0) {
      setDataLoaded(data[0].id);

      let multiplier = 1;
      if (data.length >= 3) {
        multiplier = 3;
      } else if (data.length === 2) {
        multiplier = 2;
      }
      setListContainerHeight(
        LIST_HEIGHT * multiplier + LIST_CONTAINER_BOTTOM_PADDING
      );
    } else {
      setDataLoaded('');
      setListContainerHeight(LIST_HEIGHT + LIST_CONTAINER_BOTTOM_PADDING);
    }
  }, []);
  const isDetailPage = location.pathname.includes('/activity/all');

  const skipNextSlide = isActivityOrEditPage(location);

  const handleViewAllIssues = useCallback(() => {
    if (!skipNextSlide) {
      prepareSlideTransition(SlideAction.NEXT);
    }

    if (!isDetailPage) {
      navigate(
        `${basePath}/activity/all`,
        !skipNextSlide
          ? { state: { slideLayoutInitialTransition: SlideAction.NEXT } }
          : undefined
      );
      return;
    }
  }, [navigate, basePath, prepareSlideTransition, isDetailPage, skipNextSlide]);

  const renderEmptyState = () => {
    return (
      <Button
        style={styles.addFileEmpty}
        leftIcon={<AddIcon />}
        variant={'rectangular'}
        onPress={() => {
          const newIssuePath = `${basePath}/activity/new`;
          if (!skipNextSlide) {
            prepareSlideTransition(SlideAction.NEXT);
          }
          navigate(
            newIssuePath,
            !skipNextSlide
              ? { state: { slideLayoutInitialTransition: SlideAction.NEXT } }
              : undefined
          );
        }}
      >
        {t('asset.header.buttons.addNew')}
      </Button>
    );
  };

  const handleIssueSelected = useCallback(
    (id: string) => {
      onIssueSelected?.(id);
    },
    [onIssueSelected]
  );

  return (
    <View style={styles.containerHeightCropCircle}>
      <ViewAllSectionHeader
        title={t('asset.header.activity')}
        onPress={handleViewAllIssues}
        disabled={!dataLoaded}
      />
      <View
        style={{
          marginBottom: 16,
          height: listContainerHeight,
        }}
      >
        <ActivityListBase
          onIssueSelected={handleIssueSelected}
          onDataLoaded={handleDataLoaded}
          assetId={assetId}
          excludeFilter={true}
          showTextEmpty={true}
          cardVariant="assetCompact"
          pageSize={3}
          showTopBar={false}
          filterKey={
            assetId
              ? ActivityFilterKey.AssetActivity
              : ActivityFilterKey.Activity
          }
          customEmptyState={renderEmptyState()}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  containerHeightCropCircle: {
    width: '100%',
    paddingHorizontal: tokens.spacing.lg,
    paddingTop: tokens.spacing.sm,
  },
  activityListCropCircle: {
    height: '100%',
    minHeight: 100,
    overflow: 'hidden',
    marginBottom: 0,
  },
  addFileEmpty: {
    flex: 1,
    height: 56,
    maxHeight: 56,
    backgroundColor: tokens.colors.slate[100],
    width: '100%',
  },
});
